import { isEmpty, isNil, get, orderBy } from 'lodash';
import moment from 'moment';
import { isBetweenDates } from '../../utils/date-helpers';
import { formatPercentage } from '../../utils/formatter';
import type { HouseholdPhaseInRelief } from './types';

import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import { AFFORDABLE_RELATIONSHIPS } from '../../utils/affordable';
import {
  EVICTION_CERT_TYPE,
  FSS_ADDENDUM_CERT_TYPE,
  HUD_CERT_TYPES_WITHOUT_NEXT_RECERT_DATE,
  HUD_PROGRAM_NAME,
  INITIATE_ABSENCE_CERT_TYPE,
  INTERIM_CERT_TYPE,
  MOVE_OUT_CERT_TYPE,
  PH_PROGRAM_NAME,
  RD_PROGRAM_NAME,
  RECERT_CERT_TYPE,
  TERMINATE_ABSENCE_CERT_TYPE,
  TERMINATION_CERT_TYPE,
  UNIT_TRANSFER_CERT_TYPE,
  VACATE_CERT_TYPE,
} from './constants';
import { ELIGIBILITY_UNIVERSE_CODE_ENUM } from '../../constants/hudEligibilityUniverseCodes';

// Cert types that never determine a program or calculate assistance, so the assistance-details
// gate must not apply to them. Mirrors CERT_TYPES_THAT_NEVER_CALCULATE_ASSISTANCE in
// fortress-backend src/utils/affordable.js — keep the two lists in sync.
//
// This list is deliberately not gated behind a feature flag: none of these certifications
// can ever produce an assistance determination, so gating it blocks completion outright.
const ASSISTANCE_NOT_REQUIRED_CERT_TYPES = [
  MOVE_OUT_CERT_TYPE,
  VACATE_CERT_TYPE,
  FSS_ADDENDUM_CERT_TYPE,
  EVICTION_CERT_TYPE,
  INITIATE_ABSENCE_CERT_TYPE,
  TERMINATE_ABSENCE_CERT_TYPE,
];

/**
 * Whether RD lease readiness should gate certification actions for this certification.
 *
 * In an RD lease state, Certify and Generate produces the 3560-8 and the RD lease together, and
 * Complete Certification waits for both. Cert types that never generate a lease -- eviction and
 * both absence types, which sit outside RD_LEASE_CERTIFICATION_TYPES -- have disableGenerateLease
 * permanently true, so gating them on it blocks certify and complete for good (ENG-31767).
 *
 * Single-sourced deliberately: this rule is applied by more than one button, and the bug it
 * fixes was caused by those buttons disagreeing.
 */
export const getDoesLeaseReadinessApply = ({
  isRDLeaseEnableInState,
  hideGenerateLease,
}: {
  isRDLeaseEnableInState: boolean,
  hideGenerateLease: boolean,
}): boolean => Boolean(isRDLeaseEnableInState && !hideGenerateLease);

/**
 * Whether a document-generate button should be disabled for missing/unqualified assistance details.
 *
 * `isQualified: false` carries two very different meanings. Usually it means the assistance
 * calculation could not place the household, and generating a document off that would be wrong.
 * But an RD property holding an income waiver legitimately houses tenants whose income exceeds
 * every set-aside limit; for them the backend returns a real determination -- setAsideProgramName
 * 'Does Not Qualify' -- which the 3560-8 reports as Eligibility 'A' (Above Moderate) and MINC
 * transmits as income-ineligible. Blocking on that left those households unable to certify at all,
 * because Complete Certification stays disabled until the 3560-8 exists (ENG-31781).
 *
 * `hasIneligibleDetermination` is that narrower signal, and it defaults to false so every caller
 * that does not opt in -- the HUD 50059/59A, 50058 and lease buttons -- keeps the original rule.
 * Missing assistance details still block regardless: there is nothing to report either way.
 */
export const getIsDisabledDueToAssistance = ({
  certRecordSaving,
  certificationType,
  hasQualificationAssistanceDetails,
  isQualified,
  hasIneligibleDetermination = false,
}: {
  certRecordSaving: boolean,
  certificationType: string,
  hasQualificationAssistanceDetails: boolean,
  isQualified: boolean,
  hasIneligibleDetermination?: boolean,
}): boolean =>
  Boolean(
    certRecordSaving &&
      !ASSISTANCE_NOT_REQUIRED_CERT_TYPES.includes(certificationType) &&
      (!hasQualificationAssistanceDetails ||
        (!isQualified && !hasIneligibleDetermination)),
  );

export const dropDownMapper = ({ id, name, code }) => {
  if (name) {
    return { value: id, text: name };
  } else {
    return { value: id, text: code };
  }
};

/** Default effective date for react-hook-form when opening Start Certification modal. */
export const getInitialVoucherEffectiveDateForCertModal = ({
  isRD,
  certType,
  recertDueDate,
  currentMoveOutDate,
  isPriorResident,
}: {
  isRD: boolean,
  certType: string,
  recertDueDate?: string,
  currentMoveOutDate?: string,
  isPriorResident: boolean,
}): moment.Moment | null => {
  if (isRD && certType === EVICTION_CERT_TYPE) {
    return null;
  }
  if (certType === TERMINATION_CERT_TYPE) {
    if (isPriorResident) {
      return moment.utc(currentMoveOutDate);
    }
    return moment().utc().endOf('month');
  }
  const firstOfNextMonth = moment().startOf('month').add(1, 'month');
  const hasRecertDueDate = Boolean(recertDueDate?.length);
  let defaultDateValue = moment();
  if (isRD && hasRecertDueDate) {
    defaultDateValue = moment(recertDueDate);
  } else if (certType === INTERIM_CERT_TYPE) {
    defaultDateValue = firstOfNextMonth;
  } else if (certType === RECERT_CERT_TYPE && hasRecertDueDate) {
    defaultDateValue = moment(recertDueDate);
  }
  return defaultDateValue;
};

const parseYesNoFromTrueFalse = (value: ?boolean) => {
  if (value === true) {
    return 'yes';
  }
  if (value === false) {
    return 'no';
  }
  return '';
};

export const parseTrueFalseFromYesNo = (value: string) => {
  if (value === 'yes') {
    return true;
  }
  if (value === 'no') {
    return false;
  }
  return value;
};

export const parseExemptionIncomingValues = (
  exemptions,
  householdPhaseInReliefs: Array<HouseholdPhaseInRelief>,
  effectiveDate: string,
  previousGeneralReliefAnswer,
  programName: string,
) => {
  const {
    // Yes/no values
    allLeaseSignersAbleToSign59,
    householdExemptFromEligibilityCheck,
    householdHasHUDApprovalWaiver,
    householdRequestedHardshipException,
    qualifiesForSurvivorAssistance,
    qualifyForRentOverride,
    qualifyForWelfareRent,
    priorMedicalDisabilityDeduction,
    isUnitTransfer,
    householdHasExemptIncome,
    qualifiesForGeneralRelief,
    qualifiesForChildcareHardshipExemption,
    qualifiesForMinimumRentHardshipExemption,
    section8AssistanceAfter1984,
    // Text values
    TTPbeforeOverride,
    TTPOverrideAmount,
    welfareRentAmount,
    previousUnitNumber,
    generalReliefStartDate,
    generalReliefEndDate,
    childcareHardshipExemptionStartDate,
    childcareHardshipExemptionEndDate,
    // Select option values
    affordableExtenuatingCircumstanceId,
    affordableHardshipExceptionId,
    affordableIncomeExceptionCodeId,
    affordableWaiverCodeId,
    qualifiesForPhaseInRelief,
  } = exemptions;

  const qualifiesForPhaseInReliefParsedValue = parseYesNoFromTrueFalse(
    qualifiesForPhaseInRelief,
  );
  let qualifiesForPhaseInReliefValue = qualifiesForPhaseInRelief;

  if (isEmpty(qualifiesForPhaseInReliefParsedValue)) {
    if (
      householdPhaseInReliefs?.length === 1 &&
      householdPhaseInReliefs?.[0]?.percentage === 0.1
    ) {
      qualifiesForPhaseInReliefValue = false;
    }

    if (householdPhaseInReliefs?.length > 1) {
      qualifiesForPhaseInReliefValue = true;
    }
  }

  const yesNoFields = {
    allLeaseSignersAbleToSign59: parseYesNoFromTrueFalse(
      allLeaseSignersAbleToSign59,
    ),
    hadPriorMedicalDisabilityDeduction: parseYesNoFromTrueFalse(
      priorMedicalDisabilityDeduction,
    ),
    householdExemptFromEligibilityCheck: parseYesNoFromTrueFalse(
      householdExemptFromEligibilityCheck,
    ),
    householdHasExemptIncome: parseYesNoFromTrueFalse(householdHasExemptIncome),
    householdHasHUDApprovalWaiver: parseYesNoFromTrueFalse(
      householdHasHUDApprovalWaiver,
    ),
    householdRequestedHardshipException: parseYesNoFromTrueFalse(
      householdRequestedHardshipException,
    ),
    qualifiesForSurvivorAssistance: parseYesNoFromTrueFalse(
      qualifiesForSurvivorAssistance,
    ),
    qualifyForRentOverride: parseYesNoFromTrueFalse(qualifyForRentOverride),
    qualifyForWelfareRent: parseYesNoFromTrueFalse(qualifyForWelfareRent),
    isUnitTransfer: parseYesNoFromTrueFalse(isUnitTransfer),
    qualifiesForGeneralRelief: parseYesNoFromTrueFalse(
      qualifiesForGeneralRelief,
    ),
    qualifiesForChildcareHardshipExemption: parseYesNoFromTrueFalse(
      qualifiesForChildcareHardshipExemption,
    ),
    qualifiesForPhaseInRelief: parseYesNoFromTrueFalse(
      qualifiesForPhaseInReliefValue,
    ),
    qualifiesForPhaseIn: parseYesNoFromTrueFalse(
      qualifiesForPhaseInReliefValue,
    ),
    qualifiesForMinimumRentHardshipExemption: parseYesNoFromTrueFalse(
      qualifiesForMinimumRentHardshipExemption,
    ),
    section8AssistanceAfter1984: parseYesNoFromTrueFalse(
      section8AssistanceAfter1984,
    ),
  };

  const phaseInRelief = getPhaseInReliefByDate(
    effectiveDate,
    householdPhaseInReliefs,
  );

  const phaseInPercentage = formatPercentage(
    adjustPhaseInValue(
      phaseInRelief,
      previousGeneralReliefAnswer,
      qualifiesForGeneralRelief,
      programName,
    ),
    DASHES,
  );

  return {
    ...yesNoFields,
    TTPbeforeOverride:
      TTPbeforeOverride === null || TTPbeforeOverride === undefined
        ? ''
        : String(TTPbeforeOverride),
    TTPOverrideAmount:
      TTPOverrideAmount === null || TTPOverrideAmount === undefined
        ? ''
        : String(TTPOverrideAmount),
    welfareRentAmount: welfareRentAmount ?? '',
    affordableExtenuatingCircumstanceId:
      affordableExtenuatingCircumstanceId ?? '',
    affordableHardshipExceptionId: affordableHardshipExceptionId ?? '',
    affordableIncomeExceptionCodeId: affordableIncomeExceptionCodeId ?? '',
    affordableWaiverCodeId: affordableWaiverCodeId ?? '',
    previousUnitNumber: previousUnitNumber ?? '',
    generalReliefStartDate: generalReliefStartDate,
    generalReliefEndDate: generalReliefEndDate,
    childcareHardshipExemptionStartDate: childcareHardshipExemptionStartDate,
    childcareHardshipExemptionEndDate: childcareHardshipExemptionEndDate,
    qualifiesForPhaseInRelief: qualifiesForPhaseInReliefValue,
    phaseInStartDate: phaseInRelief?.startDate,
    phaseInEndDate: isNil(phaseInRelief?.endDate)
      ? '---'
      : phaseInRelief?.endDate,
    phaseInPercentage: phaseInPercentage,
  };
};

export const parseCorrectionIncomingValues = (
  correction,
  qualificationData,
) => {
  const masterCorrectionCodeId = correction?.masterCorrectionCodeId ?? '';
  const eivIndicator = parseYesNoFromTrueFalse(qualificationData?.eivIndicator);

  return { masterCorrectionCodeId, eivIndicator };
};

export const filterAndSortHouseholdMembersWithMinors = (
  householdMembers: Object[],
): Object[] => {
  const filteredAndSortedHouseholdMembersWithMinors = filterHouseholdMembers(
    householdMembers,
    true,
  );

  // sort household members by applicant type name minor last
  filteredAndSortedHouseholdMembersWithMinors.sort((a, b) => {
    const aIsMinor = a.applicantTypeName === 'Minor';
    const bIsMinor = b.applicantTypeName === 'Minor';

    if (aIsMinor && !bIsMinor) {
      return 1;
    }
    if (!aIsMinor && bIsMinor) {
      return -1;
    }
    return 0;
  });

  return filteredAndSortedHouseholdMembersWithMinors;
};
/**
 * Applicant types that are never household members for certification, whatever their affordable
 * relationship says. Records made outside the household editor can carry the type with a blank
 * relationship, so both are checked, as the start-recert picker and the backend do.
 */
const NON_HOUSEHOLD_APPLICANT_TYPES = [
  'Guarantor Lease Signer',
  'Live-In-Caretaker',
];

export const filterHouseholdMembers = (
  householdMembers: Object[],
  includeMinors: boolean = false,
): Object[] => {
  let filteredHouseholdMembers = householdMembers ? [...householdMembers] : [];

  if (filteredHouseholdMembers.length === 0) {
    return filteredHouseholdMembers;
  }

  filteredHouseholdMembers = filteredHouseholdMembers?.filter(
    (eventHouseholdMember) => {
      const { applicantTypeName, affordableRelationship } =
        eventHouseholdMember;
      if (includeMinors && applicantTypeName === 'Minor') {
        return true;
      } else if (!includeMinors && applicantTypeName === 'Minor') {
        return false;
      }
      const isGuarantorOrLiveInCaretaker =
        NON_HOUSEHOLD_APPLICANT_TYPES.includes(applicantTypeName) ||
        [
          AFFORDABLE_RELATIONSHIPS.GUARANTOR,
          AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER,
        ].includes(affordableRelationship);
      return !isGuarantorOrLiveInCaretaker;
    },
  );

  return filteredHouseholdMembers;
};

const formatConfigurationData = (data: Object): Object => {
  // Simulating to have one row per program, whenever we are ready for it..
  const generalSetup = {
    FIPSCode: data.FIPSCode,
    affordableWaitlist: data.affordableWaitlist,
    affordableWaitlistStatus: data.affordableWaitlistStatus,
    isAutoRecertDateEnabled: data.isAutoRecertDateEnabled,
    XMLPropertyName: data.XMLPropertyName,
    XMLProjectName: data.XMLProjectName,
  };

  const NOAHSetup = {
    annualRecertMonth: data.NOAHAnnualRecertMonth,
    interimCert: data.interimCertNOAH,
    autoRolloverIncomeAndAssets: data.NOAHAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledNOAH,
    requiresInitialCertification: data.requiresInitialCertificationForNOAH,
  };

  const DeferredLoanSetup = {
    annualRecertMonth: data.DeferredLoanAnnualRecertMonth,
    interimCert: data.interimCertDeferredLoan,
    autoRolloverIncomeAndAssets: data.DeferredLoanAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled:
      data.isOverrideQualificationEnabledDeferredLoan,
    requiresInitialCertification:
      data.requiresInitialCertificationForDeferredLoan,
  };

  const VHCBSetup = {
    annualRecertMonth: data.VHCBAnnualRecertMonth,
    interimCert: data.interimCertVHCB,
    autoRolloverIncomeAndAssets: data.VHCBAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledVHCB,
    requiresInitialCertification: data.requiresInitialCertificationForVHCB,
  };

  const NSPSetup = {
    annualRecertMonth: data.NSPAnnualRecertMonth,
    interimCert: data.interimCertNSP,
    autoRolloverIncomeAndAssets: data.NSPAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledNSP,
    requiresInitialCertification: data.requiresInitialCertificationForNSP,
  };

  const AHPSetup = {
    annualRecertMonth: data.AHPAnnualRecertMonth,
    interimCert: data.interimCertAHP,
    autoRolloverIncomeAndAssets: data.AHPAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledAHP,
    requiresInitialCertification: data.requiresInitialCertificationForAHP,
  };

  const KCMOSetup = {
    annualRecertMonth: data.KCMOAnnualRecertMonth,
    interimCert: data.interimCertKCMO,
    autoRolloverIncomeAndAssets: data.KCMOAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledKCMO,
    requiresInitialCertification: data.requiresInitialCertificationForKCMO,
  };

  const StLouisFundsSetup = {
    annualRecertMonth: data.StLouisFundsAnnualRecertMonth,
    interimCert: data.interimCertStLouisFunds,
    autoRolloverIncomeAndAssets: data.StLouisFundsAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled:
      data.isOverrideQualificationEnabledStLouisFunds,
    requiresInitialCertification:
      data.requiresInitialCertificationForStLouisFunds,
  };

  const TCAPSetup = {
    annualRecertMonth: data.TCAPAnnualRecertMonth,
    interimCert: data.interimCertTCAP,
    autoRolloverIncomeAndAssets: data.TCAPAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledTCAP,
    requiresInitialCertification: data.requiresInitialCertificationForTCAP,
  };

  const LIHTCSetup = {
    annualRecertMonth: data.LIHTCAnnualRecertMonth,
    interimCert: data.interimCert,
    autoRolloverIncomeAndAssets: data.LIHTCAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabled,
    requiresInitialCertification: data.requiresInitialCertificationForNonHUD,
  };

  const HOMESetup = {
    annualRecertMonth: data.HOMEAnnualRecertMonth,
    autoRolloverIncomeAndAssets: data.HOMEAutoRolloverIncomeAndAssets,
    HOMEAffordabilityPeriodStartDate: data.HOMEAffordabilityPeriodStartDate,
    HOMEUnitType: data.HOMEUnitType,
    HOMEAnnualIncomeType: data.HOMEAnnualIncomeType,
    HOMEPropertyType: data.HOMEPropertyType,
  };

  const HUDSetup = {
    annualRecertMonth: data.HUDAnnualRecertMonth,
    autoRolloverIncomeAndAssets: data.HUDAutoRolloverIncomeAndAssets,
    HUDProjectNumber: data.HUDProjectNumber,
    HUDContractNumber: data.HUDContractNumber,
    HUDiMaxNumber: data.HUDiMaxNumber,
    IsHUDOwned: data.IsHUDOwned,
    universeCode: data.universeCode,
    planOfActionCode: data.planOfActionCode,
    hudPrimarySubsidyType: data.hudPrimarySubsidyType,
    hudSecondarySubsidyType: data.hudSecondarySubsidyType,
  };

  const RDSetup = {
    annualRecertMonth: data.RDAnnualRecertMonth,
    autoRolloverIncomeAndAssets: data.RDAutoRolloverIncomeAndAssets,
    RDProjectNumber: data.RDProjectNumber,
    RDBorrowerId: data.RDBorrowerId,
    RDProjectName: data.RDProjectName,
    isRDRAEnabled: data.isRDRAEnabled,
    rdProjectType: data.rdProjectType,
    rdProgramType: data.rdProgramType,
    loanTransferAmount: data.loanTransferAmount,
    noteRentPayment: data.noteRentPayment,
    icPayment: data.icPayment,
  };

  const BONDSetup = {
    annualRecertMonth: data.BONDAnnualRecertMonth,
    interimCert: data.interimCertBOND,
    autoRolloverIncomeAndAssets: data.BONDAutoRolloverIncomeAndAssets,
    isOverrideQualificationEnabled: data.isOverrideQualificationEnabledBOND,
    requiresInitialCertification: data.requiresInitialCertificationForBOND,
  };

  const PHSetup = {
    annualRecertMonth: data.PHAnnualRecertMonth,
    autoRolloverIncomeAndAssets: data.PHAutoRolloverIncomeAndAssets,
    agencyName: data.agencyName,
    phaCode: data.phaCode,
    phProgramType: data.PHProgramType,
    phProjectNumber: data.PHProjectNumber,
    phProjectSuffix: data.PHProjectSuffix,
    autoSendToPIC: data.autoSendToPIC,
  };

  return {
    generalSetup,
    NOAH: NOAHSetup,
    'Deferred Loan': DeferredLoanSetup,
    LIHTC: LIHTCSetup,
    HOME: HOMESetup,
    HUD: HUDSetup,
    RD: RDSetup,
    VHCB: VHCBSetup,
    NSP: NSPSetup,
    AHP: AHPSetup,
    KCMO: KCMOSetup,
    'St. Louis Funds': StLouisFundsSetup,
    TCAP: TCAPSetup,
    PH: PHSetup,
    BOND: BONDSetup,
  };
};

export const getSettingsByPropertyAndProgram = (
  configuration: Object,
  programName: string,
): Object => {
  const formattedConf = formatConfigurationData(configuration);
  // Handle layered settings
  const programNames = programName.split('/').map((name) => name.trim());

  // First, gather all configurations for each program
  const programConfigs = programNames.map((name) => formattedConf[name] || {});

  // Merge configurations with boolean OR logic for boolean values
  const availableConf = programConfigs.reduce((acc, config) => {
    Object.entries(config).forEach(([key, value]) => {
      // If the value is boolean, OR it with existing value
      if (typeof value === 'boolean') {
        acc[key] = acc[key] || value;
      }
      // For non-boolean values, take the last non-undefined value
      else if (value !== undefined) {
        acc[key] = value;
      }
    });
    return acc;
  }, {});

  return {
    ...formattedConf.generalSetup,
    ...availableConf,
  };
};

export const getPhaseInReliefByDate = (
  effectiveDate: string,
  phaseInReliefs: Array<HouseholdPhaseInRelief> = [],
): HouseholdPhaseInRelief => {
  const mEffectiveDate = moment.utc(effectiveDate);

  const phaseInRelief: HouseholdPhaseInRelief = phaseInReliefs?.find(
    (relief: HouseholdPhaseInRelief) => {
      const mStartDate = moment.utc(relief.startDate).startOf('day');
      const mEndDate = isNil(relief.endDate)
        ? moment.utc().add(1000, 'years').endOf('day')
        : moment.utc(relief.endDate);

      return isBetweenDates(mEffectiveDate, mStartDate, mEndDate, 'day');
    },
  );

  return phaseInRelief;
};

export const adjustPhaseInValue = (
  phaseInData: Object,
  previousGeneralReliefAnswer: boolean | 'yes' | 'no' | null,
  qualifiesForGeneralRelief: boolean | 'yes' | 'no',
  programName: string,
): number | null => {
  const value = get(phaseInData, 'percentage');

  if (isNil(value)) {
    return null;
  }

  if (programName !== RD_PROGRAM_NAME) {
    return value;
  }

  const hadPreviousGeneralRelief =
    previousGeneralReliefAnswer === true ||
    previousGeneralReliefAnswer === 'yes';
  const currentlyQualifiesForGeneralRelief =
    qualifiesForGeneralRelief === true || qualifiesForGeneralRelief === 'yes';

  return value === 0.075 &&
    // data coming from backend is boolean and data coming from form is yes/no
    hadPreviousGeneralRelief &&
    currentlyQualifiesForGeneralRelief
    ? 0.05
    : value;
};

/**
 * Ids of the completed certifications that currently have a correction open
 * against them. A correction points through correctedQualId at the cert it
 * supersedes -- its immediate predecessor, which may itself be a correction --
 * so that cert is "under correction" while the correction is still active.
 * One hop is exactly right here: a correction of a correction locks the
 * correction it is amending, not the family's original.
 */
export const getIdsWithOpenCorrection = (
  affordableQualifications: Object[],
): Set<string> => {
  return new Set(
    affordableQualifications
      .filter(
        (q) =>
          q?.qualificationData?.isActive === true &&
          !!q?.qualificationData?.isCorrection &&
          !!q?.qualificationData?.correctedQualId,
      )
      .map((q) => q.qualificationData.correctedQualId),
  );
};

// Reopening resets HudTenantEvent.tracsSubmitted to false even though the
// cert was previously submitted, so fall back to the most recent TRACS
// submission attempt, mirroring the backend's own resilient check
// (hasSubmittedHudTenantEventForQualification).
export const getHasSubmittedToTracs = (qualificationData: ?Object): boolean => {
  if (qualificationData?.hudTenantEvent?.tracsSubmitted) {
    return true;
  }
  const [latestTracs] = orderBy(
    qualificationData?.hudTenantEvent?.tracs || [],
    ['submissionAttempt', 'tracsSubmissionDate', 'createdAt'],
    ['desc', 'desc', 'desc'],
  );
  return (
    String(latestTracs?.tracsStatus || '')
      .trim()
      .toLowerCase() === 'submitted'
  );
};

// The cert's live TRACS submission, as opposed to whether it ever reached TRACS
// (getHasSubmittedToTracs above). Keyed on the same tracsSubmitted flag the HUD Tenant Events table
// reads, so the drawer's date and that table's status cannot contradict each other.
export const getCurrentTracsSubmissionDate = (
  qualificationData: ?Object,
): ?string => {
  if (qualificationData?.hudTenantEvent?.tracsSubmitted !== true) {
    return null;
  }

  const [latestTracs] = orderBy(
    (qualificationData?.hudTenantEvent?.tracs || []).filter(
      (row) => row?.tracsSubmissionDate,
    ),
    ['tracsSubmissionDate'],
    ['desc'],
  );

  return latestTracs?.tracsSubmissionDate ?? null;
};

export const filterOutFosterChildAdults = (members: Object[]): Object[] => {
  return members.filter(
    (member) =>
      member.affordableRelationship !==
      AFFORDABLE_RELATIONSHIPS.FOSTER_CHILD_ADULT,
  );
};

export const makeReadOnlyField = (field: Object): Object => {
  const {
    rules,
    FormLabelProps,
    RadioGroupProps,
    RadioProps,
    FormControlLabelProps,
    options,
    ...rest
  } = field;

  const ro = {
    ...rest,
    readOnly: true,
    options,
    rules: undefined,
    required: false,
    helperText: undefined,
    FormHelperTextProps: {
      ...(field.FormHelperTextProps || {}),
      sx: {
        ...(field.FormHelperTextProps?.sx || {}),
        display: 'none !important',
      },
      style: {
        display: 'none',
      },
    },
    FormLabelProps,
    RadioGroupProps,
    RadioProps,
    FormControlLabelProps,
  };

  if (field.variant === 'radioGroup') {
    ro.options = (options || field.options || []).map((opt) => ({
      ...opt,
      disabled: true,
    }));
    ro.FormLabelProps = {
      ...(FormLabelProps || {}),
      disabled: false,
      required: false,
    };
    ro.RadioGroupProps = {
      ...(RadioGroupProps || {}),
      onChange: undefined,
    };
    ro.RadioProps = {
      ...(RadioProps || {}),
      disabled: true,
      disableRipple: true,
    };
    ro.FormControlLabelProps = {
      ...(FormControlLabelProps || {}),
      disabled: true,
    };
  }

  return ro;
};

export const isGeneralAffordableNonHUD = (programName) => {
  return programName !== RD_PROGRAM_NAME && programName !== PH_PROGRAM_NAME;
};

const toUTCMs = (d) => {
  if (!d) return 0;
  const ms = Date.parse(d);
  return Number.isNaN(ms) ? 0 : ms;
};

export const sortCertificationsByFloorPlanChanges = (a, b) => {
  const aDate = a?.qualificationData?.voucherEffectiveDate;
  const bDate = b?.qualificationData?.voucherEffectiveDate;

  // 1. Effective Date DESC
  const aMs = toUTCMs(aDate);
  const bMs = toUTCMs(bDate);
  if (bMs !== aMs) return bMs - aMs;

  // 2. Entered timestamp DESC (certificationCompletionDate, fallback createdAt)
  const aEntered =
    toUTCMs(a?.qualificationData?.certificationCompletionDate) ||
    toUTCMs(a?.qualificationData?.createdAt);
  const bEntered =
    toUTCMs(b?.qualificationData?.certificationCompletionDate) ||
    toUTCMs(b?.qualificationData?.createdAt);
  if (bEntered !== aEntered) return bEntered - aEntered;

  // 3. Transfer event order: transfer IN before transfer OUT
  const aIsTransfer =
    a?.qualificationData?.certificationType === UNIT_TRANSFER_CERT_TYPE;
  const bIsTransfer =
    b?.qualificationData?.certificationType === UNIT_TRANSFER_CERT_TYPE;
  if (aIsTransfer && bIsTransfer) {
    const aIsTransferIn = a?.tenantEventData?.eventType === 'TRANSFER_IN';
    const bIsTransferIn = b?.tenantEventData?.eventType === 'TRANSFER_IN';
    if (aIsTransferIn && !bIsTransferIn) return -1;
    if (!aIsTransferIn && bIsTransferIn) return 1;
  }

  // 4. createdAt DESC (final tiebreaker)
  const aCreated = toUTCMs(a?.qualificationData?.createdAt);
  const bCreated = toUTCMs(b?.qualificationData?.createdAt);
  return bCreated - aCreated;
};

const legacyUniverseCodeToEnum = (code: ?(string | number)): ?string => {
  if (String(code) === '1') {
    return ELIGIBILITY_UNIVERSE_CODE_ENUM.PRE_1981;
  }
  if (String(code) === '2') {
    return ELIGIBILITY_UNIVERSE_CODE_ENUM.POST_1981;
  }
  return null;
};

// Eligibility universe: unit override, then the effective contract, then the setup's legacy code.
export const resolveHudEligibilityUniverseCode = ({
  unit,
  contract,
  propertySetup,
}: {
  unit?: ?Object,
  contract?: ?Object,
  propertySetup?: ?Object,
}): ?string => {
  return (
    unit?.eligibilityUniverseCode ??
    contract?.eligibilityUniverseCode ??
    legacyUniverseCodeToEnum(propertySetup?.universeCode?.code)
  );
};

export const shouldShowNextRecertificationDate = ({
  programName,
  certificationType,
}) => {
  if (programName === PH_PROGRAM_NAME) {
    return false;
  }
  if (
    programName === HUD_PROGRAM_NAME &&
    HUD_CERT_TYPES_WITHOUT_NEXT_RECERT_DATE.includes(certificationType)
  ) {
    return false;
  }
  return true;
};
